import type { StaffPatientWorkspaceData } from '../../types/plans'

export function PatientWorkspaceDetailSection({
  data,
  loading,
  error,
  onRetry,
  canIssueSecretLinks = false,
  onIssuePatientSecretLink,
}: {
  data: StaffPatientWorkspaceData | null
  loading: boolean
  error: string | null
  onRetry: () => void
  canIssueSecretLinks?: boolean
  onIssuePatientSecretLink?: (patientId: number) => void
}) {
  if (loading) {
    return <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">Загружаем карточку пациента...</section>
  }

  if (error) {
    return (
      <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <p className="mb-3 text-sm leading-6 text-slate-500">{error}</p>
        <button type="button" className="rounded-[18px] border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800 transition" onClick={onRetry}>
          Повторить
        </button>
      </section>
    )
  }

  if (!data) {
    return (
      <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <p className="text-sm leading-6 text-slate-500">Выберите пациента из списка, чтобы открыть drill-down workspace.</p>
      </section>
    )
  }

  const patientPhoneLabel = data.patient.phone_hidden ? 'Телефон скрыт правами' : (data.patient.phone ?? 'Телефон не указан')

  return (
    <section className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-sky-600">Patient drill-down</p>
          <h2 className="text-xl font-semibold text-slate-900">{data.patient.full_name}</h2>
          <p className="mt-1 text-sm leading-6 text-slate-500">{patientPhoneLabel} · {data.patient.branch_name ?? 'Филиал уточняется'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canIssueSecretLinks && onIssuePatientSecretLink ? (
            <button
              type="button"
              className="inline-flex items-center rounded-[14px] border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800 hover:bg-sky-100"
              onClick={() => onIssuePatientSecretLink(data.patient.id)}
            >
              Secret вход для пациента
            </button>
          ) : null}
          {data.patient.tags?.length
            ? data.patient.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-800">{tag}</span>
            ))
            : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <article className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Ближайших визитов</span><strong className="text-slate-900">{data.summary.upcoming_appointments}</strong></article>
        <article className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Завершенных</span><strong className="text-slate-900">{data.summary.completed_appointments}</strong></article>
        <article className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Активных планов</span><strong className="text-slate-900">{data.summary.active_plans}</strong></article>
        <article className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-white px-4 py-3 shadow-sm"><span className="text-[13px] text-slate-500">Долг</span><strong className="text-slate-900">{data.summary.outstanding_balance} ₽</strong></article>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Приемы</h3>
          <div className="grid gap-4">
            {data.appointments.map((item) => (
              <article key={item.id} className="flex flex-col gap-3 rounded-[20px] bg-slate-50 px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <strong className="text-slate-900">{item.title}</strong>
                  <p className="mt-1 text-sm leading-6 text-slate-500">{new Date(item.date).toLocaleString('ru-RU')}</p>
                </div>
                <div className="grid gap-1 lg:justify-items-end">
                  {item.doctor_name ? <span className="font-medium text-slate-900">{item.doctor_name}</span> : null}
                  <small className="text-xs uppercase tracking-[0.08em] text-slate-500">{item.status}</small>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Финансы</h3>
          <div className="grid gap-4">
            {data.financial_records.map((item) => (
              <article key={item.id} className="flex flex-col gap-3 rounded-[20px] bg-slate-50 px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <strong className="text-slate-900">{item.title}</strong>
                  <p className="mt-1 text-sm leading-6 text-slate-500">{new Date(item.date).toLocaleString('ru-RU')}</p>
                </div>
                <div className="grid gap-1 lg:justify-items-end">
                  {item.value ? <span className="font-medium text-slate-900">{item.value}</span> : null}
                  <small className="text-xs uppercase tracking-[0.08em] text-slate-500">{item.status}</small>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 rounded-[28px] border border-slate-200/70 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">История лечения и действия</h3>
        <div className="grid gap-4">
          {data.treatment_history.map((item) => (
            <article key={item.id} className="flex flex-col gap-3 rounded-[20px] bg-slate-50 px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <strong className="text-slate-900">{item.title}</strong>
                {item.description ? <p className="mt-1 text-sm leading-6 text-slate-500">{item.description}</p> : null}
              </div>
              <div className="grid gap-1 lg:justify-items-end">
                <small className="text-xs uppercase tracking-[0.08em] text-slate-500">{item.status}</small>
              </div>
            </article>
          ))}
          {data.actions.map((action) => (
            <article key={action.key} className="flex flex-col gap-3 rounded-[20px] bg-sky-50 px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <strong className="text-slate-900">{action.label}</strong>
                <p className="mt-1 text-sm leading-6 text-slate-500">{action.description}</p>
              </div>
              <div className="grid gap-1 lg:justify-items-end">
                <small className="text-xs uppercase tracking-[0.08em] text-slate-500">{action.status}</small>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
